import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProductInterface, ProductsService } from '@core/products';
import { FormControlsModule } from '@shared/form-controls/form-controls.module';
import { InteractionsModule } from '@shared/interactions';
import { catchError, debounceTime, of, startWith, switchMap, tap } from 'rxjs';
import {
    RetailMediaProductsResultComponent
} from '../retail-media-products-result/retail-media-products-result.component';
import { RetailMediaCampaignType } from '@rmn/models/enums/retail-media-campaign-type.enum';
import { WebsiteContextService } from '@core/context';
import { LoggerService } from '@core/logger/logger.service';
import { SnackBarService } from '@shared/interactions/snack-bar/snack-bar.service';

@Component({
    selector: 'rm-retail-media-products-search',
    standalone: true,
    imports: [InteractionsModule, FormControlsModule, RetailMediaProductsResultComponent],
    templateUrl: './retail-media-products-search.component.html',
    styleUrls: ['./retail-media-products-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetailMediaProductsSearchComponent {
    @Output()
    public onSelect = new EventEmitter<ProductInterface>();

    @Output()
    public onRemove = new EventEmitter<ProductInterface['id']>();

    @Input()
    public campaignType?: RetailMediaCampaignType | null;

    @Input()
    public selectedProducts!: ProductInterface[];

    protected searchForm = new FormControl();

    protected loading = false;

    constructor(
        private readonly websiteService: WebsiteContextService,
        private readonly productsService: ProductsService,
        private readonly loggerService: LoggerService,
    private readonly snackBarService: SnackBarService
    ) { }

    protected products$ = this.searchForm.valueChanges.pipe(
        debounceTime(400),
        startWith(''),
        tap(() => this.loading = true),
        switchMap(value => {
            if (!this.campaignType) {
                return of([]);
            }

            if (this.campaignType === RetailMediaCampaignType.RETAIL_MEDIA_SELF_ADVERTISING) {
                return this.websiteService.getSelectedWebsite().pipe(
                    switchMap(
                        website => this.productsService.getProductsByQuery(value, [
                            {
                                field: 'offers.feed_id_with_offer_status',
                                operator: 'eq',
                                value: `${website.id}|true`
                            }
                        ]).pipe(
                            catchError(err => {
                                this.snackBarService.alert('Wystąpił błąd podczas pobierania dostępnych produktów.');
                                this.loggerService.error('GET_PRODUCTS_FOR_RMN_SA_CAMPAIGN_ERROR', err);
                                this.loading = false;

                                return [];
                            })
                        )
                    )
                );
            }

            return this.productsService.getProductsByQuery(
                value,
                this.campaignType === RetailMediaCampaignType.RETAIL_MEDIA_INVENTORY
                    ? [{ field: 'offers.activeRMN', operator: 'eq', value: true }]
                    : null
            ).pipe(
                catchError(err => {
                    this.snackBarService.alert('Wystąpił błąd podczas pobierania dostępnych produktów.');
                    this.loggerService.error('GET_PRODUCTS_FOR_RMN_CAMPAIGN_ERROR', err);
                    this.loading = false;

                    return [];
                })
            );
        }),
        tap(() => this.loading = false)
    );

    protected isSelected(product: ProductInterface): boolean {
        return Boolean(this.selectedProducts.find(selected => selected.id === product.id));
    }

    public clearInput(): void {
        this.searchForm.setValue('');
    }
}
